/**
 * DESCARGAR EL MOCKUP.
 *
 * Quien encarga veinte camisetas para una pena necesita ensenarselo a la pena
 * antes de pagar. Sin esto hace una captura de pantalla y la comparte igual,
 * pero peor: con la interfaz del editor dentro y sin decir que es orientativo.
 *
 * Se compone en el navegador con un canvas: la foto de la prenda, encima el
 * diseno recortado a la zona, y sobre todo ello una franja con la advertencia.
 * En el servidor tendria mejor calidad, pero exigiria un trabajo mas en la cola
 * y una espera; aqui es instantaneo y para ensenarselo a alguien basta.
 *
 * La MARCA DE AGUA no es decorativa. Una imagen sin ella circula por WhatsApp y
 * acaba usandose como prueba de color, que es justo lo que no es.
 */

export interface OpcionesMockup {
  /** Foto de la prenda, ya cargada. */
  foto: HTMLImageElement;
  /** El diseno, ya pintado (el lienzo de Fabric o una imagen). */
  diseno: HTMLCanvasElement | HTMLImageElement | null;
  /** Donde cae la zona sobre la foto, en fraccion de la imagen. */
  zona: { xFrac: number; yFrac: number; anchoFrac: number; altoFrac: number };
  /** Texto de la franja inferior. */
  aviso: string;
  /** Nombre del producto y color, para el pie. */
  titulo: string;
}

const ALTO_FRANJA = 64;

/**
 * Compone el mockup y devuelve el canvas.
 *
 * Se devuelve el canvas y no directamente el fichero para poder probarlo: un
 * `toBlob` no se puede inspeccionar, y lo que importa comprobar es que la zona
 * cae donde debe.
 */
export function componerMockup(opciones: OpcionesMockup): HTMLCanvasElement {
  const { foto, diseno, zona } = opciones;
  const ancho = foto.naturalWidth || foto.width;
  const alto = foto.naturalHeight || foto.height;

  const lienzo = document.createElement('canvas');
  lienzo.width = ancho;
  lienzo.height = alto + ALTO_FRANJA;
  const ctx = lienzo.getContext('2d');
  if (!ctx) return lienzo;

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, lienzo.width, lienzo.height);
  ctx.drawImage(foto, 0, 0, ancho, alto);

  if (diseno) {
    /*
      El diseno se recorta a la zona ANTES de pintarlo: sin el recorte, una capa
      que se sale —que el editor permite con Alt— aparecería sobre el cuello o
      la manga, y quien mire la imagen creeria que asi va a salir impresa.
    */
    ctx.save();
    ctx.beginPath();
    ctx.rect(zona.xFrac * ancho, zona.yFrac * alto, zona.anchoFrac * ancho, zona.altoFrac * alto);
    ctx.clip();
    ctx.drawImage(
      diseno,
      zona.xFrac * ancho,
      zona.yFrac * alto,
      zona.anchoFrac * ancho,
      zona.altoFrac * alto,
    );
    ctx.restore();
  }

  // Franja con el aviso. Va FUERA de la foto y no encima: superpuesta taparia
  // parte del diseno, que es lo que se quiere ensenar.
  ctx.fillStyle = '#111111';
  ctx.fillRect(0, alto, ancho, ALTO_FRANJA);
  ctx.fillStyle = '#ffffff';
  ctx.font = `${Math.round(ancho * 0.028)}px system-ui, sans-serif`;
  ctx.textBaseline = 'middle';
  ctx.fillText(opciones.titulo, 16, alto + ALTO_FRANJA * 0.34);
  ctx.fillStyle = '#cccccc';
  ctx.font = `${Math.round(ancho * 0.022)}px system-ui, sans-serif`;
  ctx.fillText(opciones.aviso, 16, alto + ALTO_FRANJA * 0.7);

  return lienzo;
}

/**
 * Descarga el canvas como PNG.
 *
 * Se usa `toBlob` y no `toDataURL`: una imagen grande en base64 pasa de los
 * limites de longitud de URL de algunos navegadores y la descarga falla sin
 * decir nada.
 */
export function descargarCanvas(lienzo: HTMLCanvasElement, nombre: string): Promise<void> {
  return new Promise((resolver) => {
    lienzo.toBlob((blob) => {
      if (!blob) {
        resolver();
        return;
      }
      const url = URL.createObjectURL(blob);
      const enlace = document.createElement('a');
      enlace.href = url;
      enlace.download = nombre;
      enlace.click();
      // Liberar la URL en el siguiente ciclo: hacerlo ya cancelaria la descarga
      // en algunos navegadores.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      resolver();
    }, 'image/png');
  });
}

/** Nombre de fichero legible y sin caracteres que rompan en Windows. */
export function nombreDeFichero(producto: string, color: string): string {
  const limpio = (s: string) =>
    s
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-zA-Z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .toLowerCase();
  return `besbello-${limpio(producto)}-${limpio(color)}.png`;
}
